// Product-catalog loading + cart pricing (catalog fetch, product/OLI
// helpers, GetCardDesc/UpdateCardDesc/updateCartPrice/calculateCartPrice).
//
// Extracted verbatim from DealerForm.vue; merged back via `mixins`.
import { postOpportunityLineItem } from "@/services/api/opportunity";
import { getStripeProducts } from "@/services/api/utility";

export default {
  methods: {
    async loadProductCatalog() {
      // If already loaded, return immediately
      if (this.productsLoaded) {
        console.log("Product catalog already loaded, skipping");
        return;
      }

      // If already loading, return the existing promise (caller will await it)
      if (this.productsLoading) {
        console.log(
          "Product catalog already loading, waiting for existing call"
        );
        if (this.productCatalogPromise) {
          return this.productCatalogPromise;
        }
        return;
      }

      // IMMEDIATELY mark as loading (synchronous, prevents race condition)
      this.productsLoading = true;

      // Create and store the promise
      this.productCatalogPromise = (async () => {
        // Fetch product catalog from API
        try {
          console.log("Fetching product catalog from API...");
          console.log(
            "API URL:",
            `${process.env.VUE_APP_APIURL}/${process.env.VUE_APP_APIVER}/utility/stripeproducts`
          );

          const response = await getStripeProducts();

          console.log("API call completed successfully");
          console.log("Raw API response:", response.data);

          // Check if response has 'products' property, otherwise use response.data directly
          this.productCatalog = response.data.products || response.data;
          this.productsLoaded = true;

          console.log(
            "Product catalog loaded successfully:",
            this.productCatalog
          );

          // Initialize card price based on default selection (Gold)
          if (this.CardSelection && this.productCatalog.membership) {
            const cardKey = this.CardSelection.toLowerCase();
            if (this.productCatalog.membership[cardKey]) {
              this.card_price = this.productCatalog.membership[cardKey].price;
            }
          }

          // Recalculate cart price now that products are loaded
          this.updateCartPrice(this.CardSelection);
        } catch (error) {
          console.error("Failed to load product catalog:", error);
          this.productLoadError = error.message || "Unknown error";

          // Fallback to hardcoded values
          this.loadProductsFallback();
        } finally {
          // Mark as no longer loading
          this.productsLoading = false;
          // Clear the promise so future calls can retry if needed
          this.productCatalogPromise = null;
        }
      })();

      // Return the promise (caller will await it)
      return this.productCatalogPromise;
    },

    loadProductsFallback() {
      // Fallback to hardcoded product catalog structure
      console.warn("Using hardcoded product catalog as fallback");

      this.productCatalog = {
        membership: {
          gold: {
            sfid: "01t37000000YWRM",
            name: "Gold Card",
            price: 249.0,
            pricebook_entry_id: "01u37000000wNq8",
            stripe_product_id: null,
          },
          lake: {
            sfid: "01t37000000YWRW",
            name: "Lake Card",
            price: 169.0,
            pricebook_entry_id: "01u37000002MUok",
            stripe_product_id: null,
          },
          profmariner: {
            sfid: "01t37000000YWRq",
            name: "Professional Mariner Card",
            price: 499.0,
            pricebook_entry_id: "01u37000002PAWz",
            stripe_product_id: null,
          },
          commercial: {
            sfid: "01t37000000YWR2",
            name: "Commercial Card",
            price: 599.0,
            pricebook_entry_id: "01u37000000wNqI",
            stripe_product_id: null,
          },
        },
        trailer_care: {
          marine: {
            sfid: "01t37000000YWSA",
            name: "Trailer Care",
            price: 16.0,
            pricebook_entry_id: "01u37000002MUou",
            stripe_product_id: null,
          },
          universal: {
            sfid: "01t37000001Ruzn",
            name: "Trailer Care Plus",
            price: 30.0,
            pricebook_entry_id: "01u37000002MsSI",
            stripe_product_id: null,
          },
        },
        donation: {
          foundation: {
            sfid: "01u37000002MUoz",
            name: "Sea Tow Foundation Donation",
            price: null,
            pricebook_entry_id: "01u37000002MUoz",
            stripe_product_id: null,
            product2id: "01t37000001Rnxp",
          },
        },
        activation: {
          code: {
            sfid: "01t37000001SPpGAAW",
            name: "Activation Code Product",
            price: null,
            pricebook_entry_id: "01u37000002PRzEAAW",
            stripe_product_id: null,
          },
        },
      };

      this.productsLoaded = true;

      // Initialize card price based on default selection
      if (this.CardSelection) {
        const cardKey = this.CardSelection.toLowerCase();
        if (this.productCatalog.membership[cardKey]) {
          this.card_price = this.productCatalog.membership[cardKey].price;
        }
      }

      // Recalculate cart price
      this.updateCartPrice(this.CardSelection);
    },

    getProductIds() {
      // Helper method to get product IDs from catalog
      if (!this.productCatalog) {
        console.error("Product catalog not loaded");
        return {};
      }

      return {
        gold: this.productCatalog.membership?.gold?.sfid || "01t37000000YWRM",
        lake: this.productCatalog.membership?.lake?.sfid || "01t37000000YWRW",
        profmariner:
          this.productCatalog.membership?.profmariner?.sfid ||
          "01t37000000YWRq",
        commercial:
          this.productCatalog.membership?.commercial?.sfid || "01t37000000YWR2",
        marine:
          this.productCatalog.trailer_care?.marine?.sfid || "01t37000000YWSA",
        universal:
          this.productCatalog.trailer_care?.universal?.sfid ||
          "01t37000001Ruzn",
        donation:
          this.productCatalog.donation?.foundation?.sfid || "01u37000002MUoz",
        activation:
          this.productCatalog.activation?.code?.sfid || "01t37000001SPpGAAW",
      };
    },

    getProductDetails(productSfid) {
      // Helper method to get product details (pricebook entry, price) from catalog
      // Returns object with pricebookentryid, price, and product2id
      if (!this.productCatalog) {
        console.error("Product catalog not loaded");
        return null;
      }

      // Handle trial products specially (they're not in the catalog)
      const trialProducts = {
        "01t37000001DyWg": { name: "60 Day Free Trial", price: 0 },
        "01t37000001DyWq": { name: "95 Day Free Trial", price: 0 },
        "01t1G000002c6cJQAQ": {
          name: "60 Day Trial Active Membership",
          price: 0,
        },
        "01t1G0000032ijgQAA": {
          name: "95 Day Trial Active Membership",
          price: 0,
        },
      };

      if (trialProducts[productSfid]) {
        return {
          pricebookentryid: productSfid, // Use product ID as pricebook entry ID for trials
          price: trialProducts[productSfid].price,
          product2id: productSfid, // product2id is the trial product ID
          name: trialProducts[productSfid].name,
        };
      }

      // Search through all categories to find matching SFID
      for (const category of Object.values(this.productCatalog)) {
        for (const product of Object.values(category)) {
          if (product.sfid === productSfid) {
            return {
              pricebookentryid: product.pricebook_entry_id,
              price: product.price,
              product2id: product.product2id || product.sfid, // For donation, use product2id if available
              name: product.name,
            };
          }
        }
      }

      console.warn(`Product not found in catalog: ${productSfid}`);
      return null;
    },

    buildSelectedProducts() {
      // Builds array of selected product IDs for OLI creation
      // Used by all three OLI creation paths (renewal and new member flows)
      const selected_products = [];
      const product_ids = this.getProductIds();

      // Card Selection (Trial or standard)
      if (this.CardSelection.includes("Trial")) {
        selected_products.push(this.selected_trial_time_product);
      } else {
        selected_products.push(product_ids[this.CardSelection.toLowerCase()]);
      }

      // Trailer Care Selection
      if (this.TrailerSelection != "None" && this.TrailerSelection != "") {
        selected_products.push(
          product_ids[this.TrailerSelection.toLowerCase()]
        );
        console.log("TC product added to selected_products");
      } else {
        console.log("No TC selected");
      }

      // Donation
      if (this.donation_amount > 0) {
        selected_products.push(product_ids.donation);
        console.log("add donation product to selected_products");
      } else {
        console.log("no donation selected");
      }

      // Activation Code
      if (this.activation_codes?.value__c > 0) {
        console.log("Activation code found, adding to selected products");
        selected_products.push(product_ids.activation);
      } else {
        console.log("No activation code used");
      }

      console.log("Final selected products:", selected_products);
      return selected_products;
    },

    createOLIPromises(selectedProducts, oppGuid, isRenewal) {
      // Creates OLI records for all selected products
      // Used by all three OLI creation paths
      // Parameters:
      //   selectedProducts: Array of product IDs from buildSelectedProducts()
      //   oppGuid: Opportunity GUID
      //   isRenewal: Boolean - true for renewal flow, false for new member flow
      const product_ids = this.getProductIds();

      const oliPromises = selectedProducts.map((element, key, arr) => {
        let data = {};
        data.opportunity__heroku_external_id__c = oppGuid;
        data.quantity = 1;

        // Set product details using productCatalog
        const productDetails = this.getProductDetails(element);

        if (productDetails) {
          // Standard products from catalog
          data.pricebookentryid = productDetails.pricebookentryid;
          data.product2id = productDetails.product2id;
          data.listprice = productDetails.price || 0;

          // Set unitprice based on product type
          if (element === product_ids.activation) {
            // Activation code: negative discount up to card price
            data.unitprice = -(this.activation_codes.value__c > this.card_price
              ? this.card_price
              : this.activation_codes.value__c);
            data.boxes_cards__c = this.activation_codes.sfid;
          } else if (element === product_ids.donation) {
            // Donation uses custom amount
            data.unitprice = this.donation_amount;
          } else if (
            element === product_ids.marine ||
            element === product_ids.universal
          ) {
            // Trailer care products
            data.unitprice = this.trailering_price;
          } else {
            // Membership cards use card_price
            data.unitprice = this.card_price;
          }
        } else {
          console.log("product not found in catalog:", element);
        }

        // Apply promotion code logic for membership cards only
        const product_ids_promo = this.getProductIds();
        const isMembershipCard = [
          product_ids_promo.gold,
          product_ids_promo.lake,
          product_ids_promo.profmariner,
          product_ids_promo.commercial,
        ].includes(element);

        if (this.promotion_valid && isMembershipCard) {
          if (this.promotion_sfid != null) {
            // Get membership pricebook entry IDs from catalog
            const membershipPBEs = [
              this.productCatalog.membership?.gold?.pricebook_entry_id,
              this.productCatalog.membership?.lake?.pricebook_entry_id,
              this.productCatalog.membership?.profmariner?.pricebook_entry_id,
              this.productCatalog.membership?.commercial?.pricebook_entry_id,
            ].filter(Boolean);

            if (membershipPBEs.includes(data.pricebookentryid)) {
              data.promotion_code__c = this.promotion_sfid;
            }
          }
        } else {
          delete data.promotion_code__c;
        }

        // Mark final product
        if (Object.is(arr.length - 1, key)) {
          data.final_product__c = true;
        } else {
          data.final_product__c = false;
        }

        // Set is_renewal flag
        data.is_renewal = isRenewal;

        return postOpportunityLineItem(data);
      });

      return Promise.all(oliPromises);
    },
    GetCardDesc(cardName) {
      var co = this.CardOptions;
      var i = 0;
      for (i = 0; i < co.length; i++) {
        if (cardName == co[i].value) {
          this.card_desc = co[i].title;
        }
      }
    },
    UpdateCardDesc(event) {
      this.GetCardDesc(event);
      document.getElementById("carddescription").innerHTML = this.card_desc;
      this.updateCartPrice(event);
    },
    updateCartPrice(event) {
      var co = this.CardOptions;
      var to = this.TrailerOptions;
      var i;

      for (i = 0; i < co.length; i++) {
        if (event == co[i].value) {
          this.card_price = co[i].cost;
        }
      }

      if (event != undefined) {
        if (!event.includes("Trial") && !event.includes("Commercial")) {
          //This check resets trailing to "None" if it's a trial so that trial never needs CC unless ARB
          for (i = 0; i < to.length; i++) {
            if (event == to[i].value) {
              this.trailering_price = to[i].cost;
            }
          }
        } else {
          this.TrailerSelection = "None";
          this.trailering_price = 0;
        }
      }

      // Check to see if the promotion code works on trailering product
      console.log("Checking promotion code for trailering");
      if (this.promotion_valid) {
        console.log("Promotion is valid");
        if (this.related_product_details != null) {
          console.log("this.related_product_details != null");
          if (this.related_product_details.name === "Trailer Care") {
            console.log("this.related_product_details.name === 'Trailer Care'");

            console.log("Trailering selection is: " + this.TrailerSelection);
            if (
              this.TrailerSelection === undefined ||
              this.TrailerSelection === null ||
              this.TrailerSelection === "None"
            ) {
              console.log(
                "this.TrailerSelection !== undefined || this.TrailerSelection !== null || this.TrailerSelection !== 'None'"
              );
              console.log("Trailering selection is: " + this.TrailerSelection);

              this.$bvToast.toast(
                "This promotion code REQUIRES trailer care to be selected. Please select trailer care and try again.",
                {
                  title: "Invalid trailer care selection.",
                  variant: "danger",
                  autoHideDelay: 8000,
                }
              );
              this.resetPromotionDetails();
            }
          }
        }
      }

      // Validate activation code compatibility with current product selection
      if (this.activation_codes) {
        this.handleActivationCode(this.activation_codes.name);
      }
      this.price_total = this.calculateCartPrice();
    },
    calculateCartPrice() {
      // FSI promo code should make the membership free (case-insensitive).
      // Only the promotion_code data field is authoritative; the URL query is
      // consumed and cleared in created() so a stale ?promocode can't leak
      // across renewals in the same tab.
      if (this.promotion_code?.toUpperCase() === "FSI") {
        return 0;
      }

      let cartValue = 0;
      const cardPrice = this.card_price;

      // Calculate base cart value based on promotion type
      switch (this.promotion_type__c) {
        case "Dollar_Value_Promotion":
        case "Dollar Value Promotion":
          cartValue = cardPrice - this.promotion_value_in_dollars;
          break;
        case "Percentage Value Promotion":
        case "Percentage_Value_Promotion":
          cartValue =
            cardPrice -
            cardPrice * (this.promotion_value_percentage_discount / 100);
          break;
        case "Additional Trial Time":
          cartValue = 0;
          break;
        default:
          cartValue = cardPrice;
      }

      // Convert all values to numbers and handle NaN
      const values = {
        cartValue: parseFloat(cartValue) || 0,
        traileringPrice: parseFloat(this.trailering_price) || 0,
        donationAmount: parseFloat(this.donation_amount) || 0,
        activationCodeAmount: parseFloat(this.activation_codes?.value__c || 0),
      };

      if (values.activationCodeAmount > this.card_price) {
        values.activationCodeAmount = this.card_price;
      }

      let total =
        values.cartValue +
        values.traileringPrice +
        values.donationAmount -
        values.activationCodeAmount;

      if (total < 0) {
        total = 0;
      }

      // Check for high total amount
      if (total > 1000) {
        this.$bvToast.toast(
          "Your cart total is greater than $1000. Please check the products you have selected.",
          {
            variant: "warning",
            title: "Cart total is greater than $1000.",
            autoHideDelay: 10000,
          }
        );
      }

      // Store as number, not string (toFixed returns a string)
      this.price_total = total;

      // Auto-set funds_collected_locally when activation code covers full cost
      // and auto-renew is not enabled (no need to collect payment method)
      // if (
      //   (total === 0 || total < 0.01) &&
      //   values.activationCodeAmount > 0 &&
      //   !(this.autorenew_status === "true" || this.autorenew_status === true) &&
      //   !(this.arb_value === "true" || this.arb_value === true)
      // ) {
      //   console.log("Auto-setting funds_collected_locally=true for $0 activation code payment");
      //   this.funds_collected_locally = true;
      // }

      return total;
    },
  },
};
